/**
 * Pure geometry helpers for the matrix's SVG overlay (av-methodology-matrix-ui spec).
 *
 * Kept separate from DOM orchestration (matrix.ts) so the semantic-position
 * → SVG-coordinate mapping — and its inverse, used when a drag ends — can be
 * unit tested without a real browser layout engine. Neither function reads
 * or writes DOM state; both operate on plain rectangles.
 */

import type { ArchetypeId, RelevanceId } from "../domain/types";

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface CellRect extends Rect {
  archetypeId: ArchetypeId;
  relevance: RelevanceId;
}

export interface Point {
  x: number;
  y: number;
}

/** Center point of a rectangle — where a node representing that cell is drawn. */
export function centerOf(rect: Rect): Point {
  return { x: rect.x + rect.width / 2, y: rect.y + rect.height / 2 };
}

/**
 * Semantic position → SVG coordinates. The SVG position is *derived from*
 * the semantic position, never the other way around (spec: "SVG shall only
 * represent this state visually").
 */
export function positionToPoint(
  archetypeId: ArchetypeId,
  relevance: RelevanceId,
  cellRects: CellRect[],
): Point | null {
  const cell = cellRects.find((c) => c.archetypeId === archetypeId && c.relevance === relevance);
  return cell ? centerOf(cell) : null;
}

function squaredDistance(a: Point, b: Point): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return dx * dx + dy * dy;
}

/**
 * Given a raw drop point (e.g. where the user released a pointer) and the
 * known cell rectangles, resolve the *nearest* cell's semantic position.
 * This is the one place raw coordinates are translated back into domain
 * terms — the result is what gets persisted, never the coordinates
 * themselves (spec: "dragging resolves to semantic position, not raw
 * coordinates").
 */
export function pointToNearestCell(point: Point, cellRects: CellRect[]): CellRect | null {
  if (cellRects.length === 0) return null;
  let nearest = cellRects[0];
  let nearestDistance = squaredDistance(point, centerOf(nearest));
  for (const cell of cellRects.slice(1)) {
    const distance = squaredDistance(point, centerOf(cell));
    if (distance < nearestDistance) {
      nearest = cell;
      nearestDistance = distance;
    }
  }
  return nearest;
}

/** Node radius for a given weight (1|2|3) — weight affects size, never the cell it occupies. */
export function radiusForWeight(weight: 1 | 2 | 3): number {
  const base = 10;
  const step = 6;
  return base + (weight - 1) * step;
}

/**
 * A cell's center point is a single spot, but a cell can hold more than one
 * node (thesis Cenário 3 doesn't limit a content's position to be unique —
 * a website can have several pieces of content at the same
 * archetype/relevance). Without an offset, every node sharing a cell would
 * render exactly on top of the others (reported by the author: "se novo,
 * vão criando sobreposto"). Spreads `count` nodes vertically within the
 * cell's own height, evenly spaced and centered on the cell's midpoint —
 * caller supplies a stable index per node (e.g. iteration order of the
 * underlying node list) so positions don't jump around between redraws.
 */
export function stackedYOffsets(cellHeight: number, count: number): number[] {
  if (count <= 1) return [0];
  const padding = 16; // keep clear of the cell's border/hover outline
  const usable = Math.max(cellHeight - padding * 2, 0);
  const step = usable / count;
  return Array.from({ length: count }, (_, i) => -usable / 2 + step * (i + 0.5));
}
